import React, { useState } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import { Download, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RubricPage = () => {
  const [teams] = useLocalStorage('feud-teams', [
    { name: 'Team 1', players: [], score: 0 },
    { name: 'Team 2', players: [], score: 0 }
  ]);
  const [questions] = useLocalStorage('feud-questions', []);
  const [timerDuration] = useLocalStorage('feud-timer-duration', 30);
  const [backgroundImageUrl] = useLocalStorage('feud-background-image-url', '');
  const [theme] = useLocalStorage('feud-theme', 'default');
  const [showInfo, setShowInfo] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = "Popular Vote - Rubribc";
  }, []);

  const exportGame = () => {
    const exportedTeams = teams.map(({ score, ...team }) => team);
    const data = { teams: exportedTeams, questions, timerDuration, backgroundImageUrl, theme };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `family-feud-game.feud.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-8 border-b pb-4">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <ArrowLeft size={20} className="mr-1"/> Back to Setup
        </button>
        <h1 className="text-3xl font-bold text-center flex-1">Game Rubric</h1>
        <button 
          onClick={exportGame}
          className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded flex items-center transition-colors"
        >
          <Download size={18} className="mr-2"/> Export Game
        </button>
      </div>

      <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2 rounded-lg text-center font-medium">
        The game board is currently open in your other tab.
      </div>

      <div className="mb-8 flex justify-center">
        <button 
          onClick={() => setShowInfo(!showInfo)}
          className={`flex items-center px-6 py-3 rounded-full font-bold transition-all shadow-md ${
            showInfo 
              ? 'bg-red-500 hover:bg-red-600 text-white' 
              : 'bg-green-500 hover:bg-green-600 text-white'
          }`}
        >
          {showInfo ? (
            <>
              <EyeOff size={20} className="mr-2" /> Hide Answers
            </>
          ) : (
            <>
              <Eye size={20} className="mr-2" /> Show Answers
            </>
          )}
        </button>
      </div>

      <div className="space-y-12">
        {showInfo && questions.map((q, qIdx) => (
          <div key={qIdx} className="border p-6 rounded-lg bg-gray-50 shadow-sm">
            <h2 className="text-2xl font-bold mb-4 text-blue-800">Q{qIdx + 1}: {q.text}</h2>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b-2">
                  <th className="py-2 px-4">#</th>
                  <th className="py-2 px-4">Answer</th>
                  <th className="py-2 px-4">Responses</th>
                </tr>
              </thead>
              <tbody>
                {q.answers.map((a, aIdx) => (
                  <tr key={aIdx} className="border-b hover:bg-white transition-colors">
                    <td className="py-2 px-4 font-mono">{aIdx + 1}</td>
                    <td className="py-2 px-4 font-semibold">{a.text}</td>
                    <td className="py-2 px-4">{a.responses}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      {!showInfo && questions.length > 0 && (
        <div className="text-center py-20 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <p className="text-xl text-gray-500 italic">Game information is hidden. Click the button above to reveal.</p>
        </div>
      )}
      {questions.length === 0 && (
        <div className="text-center text-gray-500 mt-20">
          <p className="text-xl">No questions found. Setup your game first!</p>
        </div>
      )}
    </div>
  );
};

export default RubricPage;
